import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { JsonOrder } from "@/lib/types";
import { formatRelative } from "@/lib/relativeTime";
import { useScrollLock } from "@/lib/scrollLock";

interface Props {
  open: boolean;
  onClose: () => void;
  orders: JsonOrder[];
}

export const OrderHistoryDrawer = ({ open, onClose, orders }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const total = orders.length;

  const sourceLabel = (s: JsonOrder["source"]): { text: string; cls: string } => {
    if (s === "qr")
      return { text: t("historyDrawer.viaPhone", "via téléphone"), cls: "bg-primary text-white" };
    return { text: t("historyDrawer.viaTablet", "via tablette"), cls: "bg-warm text-ink" };
  };

  // Re-render every 30s to keep the relative timestamps fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // iOS-safe body scroll lock (preserves scrollY across open/close)
  useScrollLock(open);

  if (!open || typeof document === "undefined") return null;

  const subtitle =
    total > 1
      ? t("historyDrawer.subtitleMany", { count: total, defaultValue: `${total} commandes pour cette table` })
      : t("historyDrawer.subtitleOne", { count: total, defaultValue: `${total} commande pour cette table` });

  return createPortal(
    <div
      className="fixed inset-0 z-50 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={t("historyDrawer.ariaLabel", "Historique des commandes")}
    >
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={onClose}
        className="absolute inset-0 bg-ink/60"
      />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-bg flex flex-col animate-slide-up shadow-warm-lg pb-safe">
        <header className="px-5 pt-5 pb-3 border-b border-border flex items-center justify-between pt-safe">
          <div>
            <h2 className="display-font text-2xl font-semibold text-ink">
              {t("historyDrawer.heading", "Commandes envoyées")}
            </h2>
            <p className="text-sm text-ink-soft mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink"
            aria-label={t("common.close", "Fermer")}
          >
            ✕
          </button>
        </header>

        {total === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="text-5xl mb-4" aria-hidden="true">📋</div>
            <p className="text-ink-soft text-sm max-w-xs">
              {t(
                "historyDrawer.empty",
                "Aucune commande envoyée pour le moment. Vos envois s'afficheront ici dès qu'une commande sera passée."
              )}
            </p>
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-3">
            {orders.map((o, idx) => {
              const src = sourceLabel(o.source);
              const productCount = o.products?.length ?? 0;
              const itemSummary = o.products
                ?.slice(0, 3)
                .map((p: any) => `${p.quantity ?? 1}× ${p.name ?? ""}`)
                .filter(Boolean)
                .join(" · ");
              const items = o.productNbr ?? productCount;
              const itemsLabel =
                items > 1
                  ? t("historyDrawer.itemsMany", { count: items, defaultValue: `${items} articles` })
                  : t("historyDrawer.itemsOne", { count: items, defaultValue: `${items} article` });

              return (
                <li
                  key={`${o.cartSnapshotId ?? idx}`}
                  className="bg-surface rounded-card p-4 shadow-warm-sm"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm text-ink-soft">
                      {formatRelative(o.createdAt)}
                    </span>
                    <span className="display-font text-lg font-semibold tabular-nums text-ink">
                      {(o.total ?? 0).toFixed(2)} €
                    </span>
                  </div>
                  <p className="text-sm text-ink mt-1">{itemsLabel}</p>
                  {itemSummary && (
                    <p className="text-xs text-ink-muted mt-1 line-clamp-1">
                      {itemSummary}
                      {productCount > 3 ? ` · +${productCount - 3}` : ""}
                    </p>
                  )}
                  <div className="mt-3">
                    <span
                      className={`inline-block ${src.cls} rounded-pill px-2.5 py-0.5 text-xs font-medium`}
                    >
                      {src.text}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
};
